/**
 * Mon compte v2, visiteur déconnecté (SPEC AU1) — maquette 27-connexion (titre centré, carte de
 * 430 px, onglets Connexion / Créer un compte, encadré d'aide).
 *
 * Le formulaire est le composant v1 tel quel (screens/Account/Auth : Firebase ou OAuth selon le
 * réglage), RÉ-HABILLÉ par account.css (onglets, champs, boutons, liens) sans en toucher la logique.
 * Le hero de parrainage (ReferralWelcome) et les bandeaux de campagne sont ceux de la v1. Le texte
 * « mêmes identifiants » est conservé.
 */
import { useReferralContext } from 'hooks/useReferralContext';
import { Fragment } from 'react';
import { useLocation } from 'react-router';
import AuthScreen from 'screens/Account/Auth';
import { ReferralWelcome } from 'screens/Account/ReferralWelcome';
import { absoluteHttpUrl } from '../../lib/crossSite';
import { AccountShell } from './components/Ui';
import { injectedBrands } from './lib/brands';
import { ReferralNotices } from './ReferralNotices';
import { signInTitle } from './lib/session';
import { useV1AuthPatches } from './useV1AuthPatches';

/**
 * « Utilisez les mêmes identifiants sur vos comptes Dynamo et RIISE : les comptes sont communs. » : marques de la ville
 * (mondayMindbody.uxV2.brands, SPEC § 5.2), liens vers leurs sites. Revue du 05/10 : les 3 marques parisiennes étaient
 * écrites en dur (Punch promis à Bordeaux et Annecy, liens parisiens dans toutes les villes).
 */
/**
 * Accueil du site d'une marque : son adresse de réservation sans le segment « reservation/ » final (sous-dossier
 * gardé). Revue du 05/10 : `new URL(url).origin` envoyait vers https://aptekaa.dev depuis le staging
 * aptekaa.dev/dynamo-2026, et vers http://localhost en local.
 */
function brandHomeUrl(url: string): string {
  const parsed = new URL(url);
  parsed.pathname = parsed.pathname.replace(/\/reservation\/?$/, '/');
  parsed.search = '';
  parsed.hash = '';
  return parsed.href;
}

function SharedAccountsHint() {
  const brands = injectedBrands().filter((brand) => (brand.name ?? '').trim() !== '');
  if (brands.length < 2) {
    return <p className="mmv2-acct-hint">Vos identifiants sont les mêmes sur tous les sites du Monday Sports Club.</p>;
  }
  const currentSiteId = String((typeof mondayMindbody !== 'undefined' && mondayMindbody?.siteId) || '');
  const items = brands.map((brand) => {
    const url = absoluteHttpUrl(brand.url);
    const name = (brand.name ?? '').trim();
    // La marque du site : texte simple (un lien vers soi-même, dans un nouvel onglet, n'avait pas de sens).
    const isCurrent = String(brand.siteId) === currentSiteId;
    return url && !isCurrent ? (
      <a key={String(brand.siteId)} href={brandHomeUrl(url)} target="_blank" rel="noopener noreferrer">
        {name}
      </a>
    ) : (
      <span key={String(brand.siteId)}>{name}</span>
    );
  });
  return (
    <p className="mmv2-acct-hint">
      Utilisez les mêmes identifiants sur vos comptes{' '}
      {items.map((item, i) => (
        <Fragment key={i}>
          {i > 0 ? (i === items.length - 1 ? ' et ' : ', ') : null}
          {item}
        </Fragment>
      ))}
      &nbsp;: les comptes sont communs.
    </p>
  );
}

/** = screens/Account/index.tsx AUTH_SCROLL_TARGET_ID : cible du CTA du hero de parrainage. */
const AUTH_SCROLL_TARGET_ID = 'msc-auth-form';

export function AccountSignedOut() {
  const referralContext = useReferralContext();
  const location = useLocation();
  // Intention : page d'où vient le visiteur (panier : onLoginRedirect posé par le Nouveau Panier ; pages de Mon compte
  // réservées aux membres : signInFrom, routes de la v2), sinon la page courante.
  const state = location.state as { onLoginRedirect?: string; signInFrom?: string } | null;
  const intent = state?.onLoginRedirect ?? state?.signInFrom ?? location.pathname;
  // Ancre de la page courante (#parrainage) : seulement quand l'intention EST la page courante.
  const anchor = intent === location.pathname ? location.hash : '';
  const authRef = useV1AuthPatches();
  const showWelcome =
    referralContext.hasReferral && (referralContext.mode === 'legacy' || referralContext.campaignStatus === 'active');

  return (
    <AccountShell className="mmv2-acct--signin">
      {showWelcome ? (
        <div className="mmv2-acct-welcome">
          <ReferralWelcome context={referralContext} scrollTargetId={AUTH_SCROLL_TARGET_ID} />
        </div>
      ) : null}
      <div className="mmv2-acct-signin">
        <p className="mmv2-kicker mmv2-acct-signin__kicker">Mon compte</p>
        <h1 className="mmv2-acct-signin__title">{signInTitle(intent, anchor)}</h1>
        <ReferralNotices context={referralContext} />
        <div className="mmv2-card mmv2-acct-auth" id={AUTH_SCROLL_TARGET_ID} ref={authRef}>
          <AuthScreen />
        </div>
        <SharedAccountsHint />
      </div>
    </AccountShell>
  );
}
